import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, TrendingUp, Target, Newspaper, Upload, Youtube, Link, Video, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminInsightsPage = () => {
    const { language } = useLanguage();
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        title: '', title_en: '', content: '', content_en: '',
        category: 'weekly_bias', priority: 'medium', valid_until: '', video_url: ''
    });

    // Video upload states
    const [videoType, setVideoType] = useState('none'); // none, upload, youtube
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const videoInputRef = useRef(null);

    const categories = [
        { value: 'weekly_bias', label: 'Weekly Bias', icon: TrendingUp },
        { value: 'key_zones', label: 'Key Zones', icon: Target },
        { value: 'news_impact', label: 'News Impact', icon: Newspaper }
    ];

    const priorities = [
        { value: 'high', label: 'High', color: 'bg-red-500' },
        { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
        { value: 'low', label: 'Low', color: 'bg-green-500' }
    ];

    useEffect(() => {
        fetchInsights();
    }, []);

    const getAuthConfig = () => {
        const token = localStorage.getItem('nusatrade_token');
        return { headers: { 'Authorization': `Bearer ${token}` } };
    };

    const fetchInsights = async () => {
        try {
            const response = await axios.get(`${API}/market-insights`);
            setInsights(response.data);
        } catch (error) {
            console.error('Error fetching insights:', error);
        } finally {
            setLoading(false);
        }
    };

    // Convert YouTube URL to embed format
    const convertYoutubeUrl = (url) => {
        if (!url) return '';
        
        // Handle youtube.com/shorts/VIDEO_ID format
        const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
        if (shortsMatch) {
            return `https://www.youtube.com/embed/${shortsMatch[1]}`;
        }
        
        // Handle standard youtube.com/watch?v=VIDEO_ID format
        const watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
        if (watchMatch) {
            return `https://www.youtube.com/embed/${watchMatch[1]}`;
        }
        
        // Handle youtu.be/VIDEO_ID format
        const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
        if (shortMatch) {
            return `https://www.youtube.com/embed/${shortMatch[1]}`;
        }
        
        // Already embed format
        if (url.includes('youtube.com/embed/')) {
            return url;
        }
        
        return url;
    };

    const handleYoutubeUrlChange = (url) => {
        setYoutubeUrl(url);
        if (url) {
            const embedUrl = convertYoutubeUrl(url);
            setForm({ ...form, video_url: embedUrl });
        } else {
            setForm({ ...form, video_url: '' });
        }
    };

    // Video upload handler
    const handleVideoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime', 'video/x-msvideo'];
        if (!allowedTypes.includes(file.type)) {
            toast.error(language === 'id' 
                ? 'Format tidak disokong. Gunakan MP4, WebM, OGG, MOV, atau AVI'
                : 'Unsupported format. Use MP4, WebM, OGG, MOV, or AVI'
            );
            return;
        }

        if (file.size > 500 * 1024 * 1024) {
            toast.error(language === 'id' 
                ? 'Fail terlalu besar. Maksimum 500MB'
                : 'File too large. Maximum 500MB'
            );
            return;
        }

        setUploading(true);
        setUploadProgress(0);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const token = localStorage.getItem('nusatrade_token');
            const response = await axios.post(`${API}/admin/upload/video`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                },
            });

            if (response.data.success) {
                setForm({ ...form, video_url: response.data.video_url });
                toast.success(language === 'id' ? 'Video berjaya dimuat naik!' : 'Video uploaded successfully!');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.detail || (language === 'id' ? 'Gagal muat naik video' : 'Failed to upload video'));
        } finally {
            setUploading(false);
            setUploadProgress(0);
            if (videoInputRef.current) {
                videoInputRef.current.value = '';
            }
        }
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('nusatrade_token');
            if (!token) {
                toast.error(language === 'id' ? 'Sila login semula' : 'Please login again');
                return;
            }
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            
            if (editing) {
                await axios.put(`${API}/admin/market-insights/${editing.id}`, form, config);
                toast.success(language === 'id' ? 'Insight diupdate!' : 'Insight updated!');
            } else {
                await axios.post(`${API}/admin/market-insights`, form, config);
                toast.success(language === 'id' ? 'Insight ditambahkan!' : 'Insight added!');
            }
            setDialogOpen(false);
            setEditing(null);
            resetForm();
            fetchInsights();
        } catch (error) {
            console.error('Save insight error:', error);
            toast.error(language === 'id' ? 'Gagal simpan insight' : 'Error saving insight');
        }
    };

    const handleDelete = async (id) => {
        if (!confirm(language === 'id' ? 'Yakin hapus insight ini?' : 'Delete this insight?')) return;
        try {
            const token = localStorage.getItem('nusatrade_token');
            const config = { headers: { 'Authorization': `Bearer ${token}` } };
            await axios.delete(`${API}/admin/market-insights/${id}`, config);
            toast.success(language === 'id' ? 'Insight dihapus!' : 'Insight deleted!');
            fetchInsights();
        } catch (error) {
            toast.error('Error deleting insight');
        }
    };

    const resetForm = () => {
        setForm({
            title: '', title_en: '', content: '', content_en: '',
            category: 'weekly_bias', priority: 'medium', valid_until: '', video_url: ''
        });
        setVideoType('none');
        setYoutubeUrl('');
    };

    const openEdit = (insight) => {
        setEditing(insight);
        setForm({
            title: insight.title || '',
            title_en: insight.title_en || '',
            content: insight.content || '',
            content_en: insight.content_en || '',
            category: insight.category || 'weekly_bias',
            priority: insight.priority || 'medium',
            valid_until: insight.valid_until || '',
            video_url: insight.video_url || ''
        });
        
        // Determine video type
        if (insight.video_url) {
            if (insight.video_url.includes('youtube.com')) {
                setVideoType('youtube');
                setYoutubeUrl(insight.video_url);
            } else if (insight.video_url) {
                setVideoType('upload');
            }
        } else {
            setVideoType('none');
        }
        
        setDialogOpen(true);
    };

    const getCategoryIcon = (cat) => {
        const found = categories.find(c => c.value === cat);
        return found ? found.icon : TrendingUp;
    };

    const clearVideo = () => {
        setForm({ ...form, video_url: '' });
        setVideoType('none');
        setYoutubeUrl('');
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                            <TrendingUp className="w-6 h-6 text-primary" />
                            {language === 'id' ? 'Kelola Market Insights' : 'Manage Market Insights'}
                        </h1>
                        <p className="text-slate-400 mt-1">{insights.length} insights</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => { setEditing(null); resetForm(); }} data-testid="add-insight-btn" className="bg-primary hover:bg-primary/90">
                                <Plus className="w-4 h-4 mr-2" />
                                {language === 'id' ? 'Tambah Insight' : 'Add Insight'}
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>
                                    {editing ? (language === 'id' ? 'Edit Insight' : 'Edit Insight') : (language === 'id' ? 'Tambah Insight' : 'Add Insight')}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Title (ID)</Label>
                                        <Input
                                            value={form.title}
                                            onChange={(e) => setForm({...form, title: e.target.value})}
                                            className="bg-slate-800 border-slate-700"
                                            data-testid="insight-title"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Title (EN)</Label>
                                        <Input
                                            value={form.title_en}
                                            onChange={(e) => setForm({...form, title_en: e.target.value})}
                                            className="bg-slate-800 border-slate-700"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Content (ID)</Label>
                                    <Textarea
                                        value={form.content}
                                        onChange={(e) => setForm({...form, content: e.target.value})}
                                        className="bg-slate-800 border-slate-700 min-h-[80px]"
                                        data-testid="insight-content"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Content (EN)</Label>
                                    <Textarea
                                        value={form.content_en}
                                        onChange={(e) => setForm({...form, content_en: e.target.value})}
                                        className="bg-slate-800 border-slate-700 min-h-[80px]"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Category</Label>
                                        <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                                            <SelectTrigger className="bg-slate-800 border-slate-700">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-800 border-slate-700">
                                                {categories.map(c => (
                                                    <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Priority</Label>
                                        <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                                            <SelectTrigger className="bg-slate-800 border-slate-700">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-800 border-slate-700">
                                                {priorities.map(p => (
                                                    <SelectItem key={p.value} value={p.value} className="text-white">{p.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Video Section */}
                                <div className="space-y-3 border-t border-slate-700 pt-4">
                                    <Label className="flex items-center gap-2">
                                        <Video className="w-4 h-4 text-primary" />
                                        Video (Optional)
                                    </Label>
                                    
                                    {/* Video Type Selection */}
                                    <div className="grid grid-cols-3 gap-2">
                                        <Button
                                            type="button"
                                            variant={videoType === 'none' ? 'default' : 'outline'}
                                            onClick={() => { setVideoType('none'); clearVideo(); }}
                                            className={`${videoType === 'none' ? 'bg-primary' : 'bg-slate-800 border-slate-700'}`}
                                        >
                                            <X className="w-4 h-4 mr-1" />
                                            Tiada
                                        </Button>
                                        <Button
                                            type="button"
                                            variant={videoType === 'upload' ? 'default' : 'outline'}
                                            onClick={() => setVideoType('upload')}
                                            className={`${videoType === 'upload' ? 'bg-primary' : 'bg-slate-800 border-slate-700'}`}
                                        >
                                            <Upload className="w-4 h-4 mr-1" />
                                            Upload
                                        </Button>
                                        <Button
                                            type="button"
                                            variant={videoType === 'youtube' ? 'default' : 'outline'}
                                            onClick={() => setVideoType('youtube')}
                                            className={`${videoType === 'youtube' ? 'bg-primary' : 'bg-slate-800 border-slate-700'}`}
                                        >
                                            <Youtube className="w-4 h-4 mr-1" />
                                            YouTube
                                        </Button>
                                    </div>

                                    {/* Upload Option */}
                                    {videoType === 'upload' && (
                                        <div className="space-y-2">
                                            <input
                                                type="file"
                                                ref={videoInputRef}
                                                onChange={handleVideoUpload}
                                                accept="video/*"
                                                className="hidden"
                                            />
                                            {!form.video_url ? (
                                                <Button
                                                    type="button"
                                                    onClick={() => videoInputRef.current?.click()}
                                                    disabled={uploading}
                                                    className="w-full bg-slate-800 border-slate-700 border-dashed border-2 h-20 hover:bg-slate-700"
                                                    variant="outline"
                                                >
                                                    {uploading ? (
                                                        <div className="flex flex-col items-center">
                                                            <span>Uploading... {uploadProgress}%</span>
                                                            <div className="w-32 h-2 bg-slate-700 rounded-full mt-2">
                                                                <div 
                                                                    className="h-full bg-primary rounded-full transition-all"
                                                                    style={{ width: `${uploadProgress}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center">
                                                            <Upload className="w-6 h-6 mb-1" />
                                                            <span className="text-sm">Klik untuk upload video (Max 500MB)</span>
                                                        </div>
                                                    )}
                                                </Button>
                                            ) : (
                                                <div className="flex items-center gap-2 p-3 bg-slate-800 rounded-lg">
                                                    <Video className="w-5 h-5 text-emerald-400" />
                                                    <span className="text-sm text-emerald-400 flex-1 truncate">Video uploaded</span>
                                                    <Button size="sm" variant="ghost" onClick={clearVideo} className="text-red-400 hover:text-red-300">
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* YouTube Option */}
                                    {videoType === 'youtube' && (
                                        <div className="space-y-2">
                                            <div className="flex gap-2">
                                                <Input
                                                    value={youtubeUrl}
                                                    onChange={(e) => handleYoutubeUrlChange(e.target.value)}
                                                    placeholder="https://youtube.com/watch?v=... atau youtube.com/shorts/..."
                                                    className="bg-slate-800 border-slate-700 flex-1"
                                                />
                                                {form.video_url && (
                                                    <Button size="icon" variant="ghost" onClick={clearVideo} className="text-red-400">
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                            {form.video_url && form.video_url.includes('youtube.com') && (
                                                <div className="aspect-video rounded-lg overflow-hidden bg-slate-800">
                                                    <iframe
                                                        src={form.video_url}
                                                        className="w-full h-full"
                                                        allowFullScreen
                                                        title="YouTube Preview"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
                                    <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-slate-800 border-slate-700">
                                        {language === 'id' ? 'Batal' : 'Cancel'}
                                    </Button>
                                    <Button onClick={handleSave} data-testid="save-insight-btn" className="bg-primary hover:bg-primary/90">
                                        {language === 'id' ? 'Simpan' : 'Save'}
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Insights List */}
                <div className="grid gap-4">
                    {insights.length === 0 ? (
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="py-12 text-center">
                                <TrendingUp className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <p className="text-slate-400">{language === 'id' ? 'Belum ada insight' : 'No insights yet'}</p>
                            </CardContent>
                        </Card>
                    ) : (
                        insights.map((insight) => {
                            const Icon = getCategoryIcon(insight.category);
                            const priority = priorities.find(p => p.value === insight.priority);
                            const hasVideo = insight.video_url && insight.video_url.length > 0;
                            return (
                                <Card key={insight.id} className="bg-slate-800/50 border-slate-700" data-testid={`insight-${insight.id}`}>
                                    <CardContent className="py-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-4 flex-1">
                                                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                                                    <Icon className="w-6 h-6 text-amber-400" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <h3 className="font-semibold text-white">{insight.title}</h3>
                                                        <span className={`px-2 py-0.5 rounded text-xs text-white ${priority?.color || 'bg-slate-500'}`}>
                                                            {priority?.label || insight.priority}
                                                        </span>
                                                        {hasVideo && (
                                                            <span className="px-2 py-0.5 rounded text-xs bg-primary/20 text-primary flex items-center gap-1">
                                                                <Video className="w-3 h-3" />
                                                                Video
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-slate-400 text-sm line-clamp-2">{insight.content}</p>
                                                    <p className="text-slate-500 text-xs mt-2">
                                                        Category: {categories.find(c => c.value === insight.category)?.label || insight.category}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => openEdit(insight)}
                                                    className="text-slate-400 hover:text-white"
                                                    data-testid={`edit-insight-${insight.id}`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => handleDelete(insight.id)}
                                                    className="text-slate-400 hover:text-red-400"
                                                    data-testid={`delete-insight-${insight.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })
                    )}
                </div>
            </div>
        </AdminLayout>
    );
};
